import { isAxiosError, isCancel } from 'axios';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { useWorkflowStore } from '@/state/workflow.state';
import type { ResolvedAssets } from '@/types/api/assets';

/** Balances returned after a charge or refund; null means an unlimited user allowance. */
export interface ModelRunCredits {
  remainingCredits?: number | null;
  userRemainingCredits?: number | null;
}

interface SuccessPrediction extends ModelRunCredits {
  status: 'succeeded';
  results: any[];
  resolvedAssets?: ResolvedAssets;
}

interface FailedPrediction extends ModelRunCredits {
  status: 'failed';
  error: any;
}

interface CanceledPrediction extends ModelRunCredits {
  status: 'canceled';
}

interface ProcessingPrediction {
  status: 'starting' | 'processing' | 'initial_processing';
  progress?: number;
}

type PredictionStatus = SuccessPrediction | FailedPrediction | CanceledPrediction | ProcessingPrediction;

export interface PollCallbacks {
  onRequestStart?: () => void;
  onProgress?: (progress: number) => void;
  onSuccess?: (results: any[], remainingCredits?: number | null) => void;
  onError?: (error: any, remainingCredits?: number | null) => void;
  onStatusChange?: (status: string, remainingCredits?: number | null, userRemainingCredits?: number | null) => void;
}

export const pollPredictionStatus = async (
  predictionId: string,
  callbacks: PollCallbacks,
  isCanceled: () => boolean = () => false,
  signal?: AbortSignal,
  options: { retryTransientErrors?: boolean } = {},
): Promise<void> => {
  const startTime = Date.now();
  let consecutiveErrors = 0;

  const poll = async () => {
    if (isCanceled() || signal?.aborted) {
      return;
    }

    try {
      callbacks.onRequestStart?.();
      const response = await safeAxios.get<PredictionStatus>(API_ROUTES.models.predictionStatus({ predictionId }), {
        signal,
      });

      consecutiveErrors = 0;
      if (response.data.status === 'processing' && !('progress' in response.data)) {
        response.data.status = 'initial_processing';
      }

      callbacks.onStatusChange?.(
        response.data.status,
        'remainingCredits' in response.data ? response.data.remainingCredits : undefined,
        'userRemainingCredits' in response.data ? response.data.userRemainingCredits : undefined,
      );

      if (isCanceled() || signal?.aborted) {
        return;
      }

      switch (response.data.status) {
        case 'succeeded': {
          useWorkflowStore.getState().mergeResolvedAssets(response.data.resolvedAssets ?? {});
          callbacks.onSuccess?.(response.data.results, response.data.remainingCredits);
          return; // Exit polling
        }

        case 'starting':
        case 'processing': {
          callbacks.onProgress?.(response.data.progress || 0);
          break;
        }

        case 'failed': {
          callbacks.onError?.(response.data.error, response.data.remainingCredits);
          return; // Exit polling
        }

        case 'canceled': {
          return; // Exit polling
        }
      }

      // Calculate next polling interval
      const elapsedTime = Date.now() - startTime;
      let interval = 1000; // Default 1s
      if (elapsedTime < 10000) {
        interval = 1000;
      } else if (elapsedTime < 30000) {
        interval = 2500;
      } else {
        interval = 5000;
      }

      // Schedule next poll
      setTimeout(() => void poll(), interval);
    } catch (error: any) {
      if (isCanceled() || signal?.aborted || isCancel(error)) return;
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const transient =
        isAxiosError(error) &&
        (!error.response || status === 408 || status === 429 || (status !== undefined && status >= 500));
      if (options.retryTransientErrors && transient) {
        const delay = Math.min(1000 * 2 ** Math.min(consecutiveErrors++, 5), 30_000);
        setTimeout(() => void poll(), delay);
        return;
      }
      callbacks.onError?.(error?.message);
    }
  };

  // Start polling
  await poll();
};
